"use client";

import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import styles from "./SidebarDisclosure.module.css";

export function SidebarDisclosure({
  open,
  id,
  children,
  unmountOnExit = false,
}: {
  open: boolean;
  id: string;
  children: ReactNode;
  unmountOnExit?: boolean;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [previousOpen, setPreviousOpen] = useState(open);
  const [phase, setPhase] = useState(open ? "open" : "closed");

  if (previousOpen !== open) {
    setPreviousOpen(open);
    setPhase(open ? "opening" : "closing");
  }

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    if (phase === "open" || phase === "closed") {
      viewport.style.height = open ? "auto" : "0px";
      return;
    }

    const duration = Number.parseFloat(getComputedStyle(viewport).transitionDuration) * 1000;
    let timer: number | undefined;
    let targetHeight = -1;

    const updateHeight = () => {
      const nextHeight = open ? content.getBoundingClientRect().height : 0;
      if (nextHeight === targetHeight) return;
      targetHeight = nextHeight;

      // Start from the visible height, including when a click reverses a transition.
      viewport.style.height = `${viewport.getBoundingClientRect().height}px`;
      void viewport.offsetHeight;
      viewport.style.height = `${nextHeight}px`;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setPhase(open ? "open" : "closed"), duration + 50);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(content);

    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [open, phase]);

  return (
    <div
      ref={viewportRef}
      id={id}
      className={styles.disclosure}
      data-open={open}
      data-state={phase}
      aria-hidden={!open}
      inert={!open}
      onTransitionEnd={(event) => {
        if (event.target === event.currentTarget && event.propertyName === "height") {
          setPhase(open ? "open" : "closed");
        }
      }}
    >
      <div className={styles.clip}>
        <div ref={contentRef} className={styles.content}>
          {!unmountOnExit || phase !== "closed" ? children : null}
        </div>
      </div>
    </div>
  );
}
